import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { type ProductData, ProductType } from "~/domains/product/types/ProductData";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { productsActions } from "~/domains/product/redux/pnaReducers";
import type { RootState } from "~/app/store";
import { getLinearPriceInfo } from "~/domains/linear/api/GetLinearPriceInfoSingle";
import { getCacheKey, selectProductData, selectProductDescriptor } from "~/domains/product/redux/selectors";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { getHybrisProductCode } from "~/utils/cartHelper";
import { requestAvailabilityDataSingle } from "~/domains/product/api/availabilityService";
import { requestPriceDataSingle } from "~/domains/pricing/api/priceService";
import { getLinearAvailabilityInfo } from "~/domains/linear/api/GetLinearAvailabilityInfoSingle";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { ADD_PRODUCT_BASIC, addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";

export { ADD_PRODUCT_BASIC, addBaseProductDataAction };

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

// Availability calls in progress
const aPromisesInFlight: { [code: string]: Promise<AvailabilityInfo> } = {};
// Price calls in progress
const pPromisesInFlight: { [code: string]: Promise<PriceInfo[]> } = {};

// ─── Throttled flush queue for bulk PnA results ───────────────────────────────
// Individual API results are enqueued as they arrive. Every FLUSH_THROTTLE_MS the
// queue flushes a single bulkFinish dispatch (one Redux update / one re-render)
// and then resolves the corresponding deferred promises so that
// orchestratePnARefreshRegular sees populated state via getState().
const FLUSH_THROTTLE_MS = 1000;

interface PnAFlushEntry<T> {
  cacheKey: string;
  result: { ok: true; data: T } | { ok: false };
  deferred: { resolve: (d: T) => void; reject: (e: unknown) => void };
}

function createPnAFlushQueue<T>(
  bulkFinish: (data: Record<string, T>, errorKeys: string[]) => AnyAction,
  promisesInFlight: Record<string, Promise<T>>,
) {
  let pending: PnAFlushEntry<T>[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dispatchRef: ((...args: any[]) => any) | null = null;

  function flush() {
    timer = null;
    const batch = pending;
    const dispatch = dispatchRef;
    pending = [];
    if (!dispatch || batch.length === 0) {
      return;
    }

    const successData: Record<string, T> = {};
    const errorKeys: string[] = [];
    for (const { cacheKey, result } of batch) {
      if (result.ok) {
        successData[cacheKey] = result.data;
      } else {
        errorKeys.push(cacheKey);
      }
    }

    // Single batch Redux update for all results that arrived in this throttle window.
    dispatch(bulkFinish(successData, errorKeys));

    // Resolve/reject deferreds AFTER dispatch so getState() returns populated data
    // when orchestratePnARefreshRegular continues after its awaited promise.
    for (const { cacheKey, result, deferred } of batch) {
      delete promisesInFlight[cacheKey];
      if (result.ok) {
        deferred.resolve(result.data);
      } else {
        deferred.reject(new Error("fetch failed"));
      }
    }
  }

  return {
    enqueue(
      entry: PnAFlushEntry<T>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch: (...args: any[]) => any,
    ) {
      dispatchRef = dispatch;
      pending.push(entry);
      if (timer === null) {
        timer = setTimeout(flush, FLUSH_THROTTLE_MS);
      }
    },
  };
}

const priceFlushQueue = createPnAFlushQueue<PriceInfo[]>(productsActions.price.bulkFinish, pPromisesInFlight);

const availabilityFlushQueue = createPnAFlushQueue<AvailabilityInfo>(
  productsActions.availability.bulkFinish,
  aPromisesInFlight,
);

/**
 *
 * @param productOrCode - code of the product to load
 * @param priceReference -
 * @param retryIfError - try loading again if the previous attempt failed
 */
export function requestPriceData(
  productOrCode: ProductData | string,
  priceReference: string,
  retryIfError?: boolean,
  packCode?: string,
  forceRefresh?: boolean,
): ThunkResult<Promise<PriceInfo[]>> {
  return (dispatch, getState) => {
    const state = getState();
    const product = typeof productOrCode === "string" ? selectProductData(productOrCode)(state) : productOrCode;
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: priceReference });

    const entry = state.products.price[cacheKey];

    if (!userHasPermission(Permission.PRICE)) {
      if (!entry) {
        const data: PriceInfo[] = [];
        dispatch(productsActions.price.success(cacheKey, []));
        return Promise.resolve(data);
      }
      return Promise.resolve(entry.data!);
    }

    if (entry && (!forceRefresh || entry.state === "loading")) {
      const promiseInFlight: Promise<PriceInfo[]> | undefined = pPromisesInFlight[cacheKey];
      if (entry.data) {
        return Promise.resolve(entry.data);
      } else if (entry.state === "error" && !retryIfError) {
        return Promise.reject(new Error("error"));
      } else if (entry.state === "loading" || promiseInFlight) {
        return promiseInFlight;
      }
    }

    const promise = dispatch(fetchPriceAction(product, priceReference, packCode));
    pPromisesInFlight[cacheKey] = promise;
    return promise;
  };
}

function fetchPriceAction(
  product: ProductData,
  priceReference: string,
  packCode?: string,
): ThunkResult<Promise<PriceInfo[]>> {
  return async (dispatch) => {
    // We use pnaCode to fetch, but it should be cached under code - so that the fake product-bundle can find the data
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: priceReference });

    dispatch(productsActions.price.load(cacheKey));

    let promise;
    if (product.type === ProductType.CONFIGURATOR) {
      promise = getLinearPriceInfo(getHybrisProductCode(product.code), product.configId, priceReference);
    } else {
      promise = requestPriceDataSingle({
        productCode: product.pnaCode ?? product.code,
        priceReference,
        packCode,
      });
    }

    promise
      .then((info) => {
        dispatch(productsActions.price.success(cacheKey, info));
        return info;
      })
      .catch((err) => {
        dispatch(productsActions.price.error(cacheKey));
        throw err;
      });

    promise.finally(() => delete pPromisesInFlight[cacheKey]).catch(() => delete pPromisesInFlight[cacheKey]);

    return promise;
  };
}

/**
 *
 * @param productOrCode - code of the product to load
 * @param retryIfError - try loading again if the previous attempt failed
 */
export function requestAvailabilityData(
  productOrCode: ProductData | string,
  retryIfError?: boolean,
  packCode?: string,
  forceRefresh?: boolean,
): ThunkResult<Promise<AvailabilityInfo | undefined>> {
  return (dispatch, getState) => {
    const state = getState();
    const product = typeof productOrCode === "string" ? selectProductData(productOrCode)(state) : productOrCode;
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: "" });

    const entry = getState().products.availability[cacheKey];

    if (!userHasPermission(Permission.AVAILABILITY)) {
      if (!entry) {
        dispatch(productsActions.availability.success(cacheKey, undefined));
        return Promise.resolve(undefined);
      }
      return Promise.resolve(entry.data!);
    }

    if (entry && (!forceRefresh || entry.state === "loading")) {
      const promiseInFlight: Promise<AvailabilityInfo> | undefined = aPromisesInFlight[cacheKey];
      if (entry.data) {
        return Promise.resolve(entry.data);
      } else if (entry.state === "error" && !retryIfError) {
        return Promise.reject(new Error("error"));
      } else if (entry.state === "loading" || promiseInFlight) {
        return promiseInFlight;
      }
    }

    const promise = dispatch(fetchAvailabilityAction(product, packCode));
    aPromisesInFlight[cacheKey] = promise;
    return promise;
  };
}

function fetchAvailabilityAction(product: ProductData, packCode?: string): ThunkResult<Promise<AvailabilityInfo>> {
  return (dispatch) => {
    // We use pnaCode to fetch, but it should be cached under code - so that the fake product-bundle can find the data
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: "" });

    dispatch(productsActions.availability.load(cacheKey));

    let promise;
    if (product.type === ProductType.CONFIGURATOR) {
      promise = getLinearAvailabilityInfo(product.pnaCode, product.configId);
    } else {
      promise = requestAvailabilityDataSingle({ productCode: product.pnaCode ?? product.code, packCode });
    }

    promise
      .then((info) => {
        dispatch(productsActions.availability.success(cacheKey, info));
        return info;
      })
      .catch((err) => {
        dispatch(productsActions.availability.error(cacheKey));
        throw err;
      });

    promise.finally(() => delete aPromisesInFlight[cacheKey]).catch(() => delete aPromisesInFlight[cacheKey]);
    return promise;
  };
}

interface RequestFullProductDataOptions {
  forceFetchBasicData?: boolean;
  fetchPrice?: boolean;
  fetchAvailability?: boolean;
}

export function requestFullProductData(
  code: string,
  priceReference: string,
  options: RequestFullProductDataOptions = {},
): ThunkResult<Promise<ProductDescriptor>> {
  return async (dispatch, getState) => {
    const state = getState();

    priceReference = priceReference === "-" ? "" : priceReference;

    let product = state.products.basic[code];
    if (!product || (options.forceFetchBasicData && !product?.isFullProduct)) {
      product = await fetchProductInfo(code);
      dispatch(addBaseProductDataAction(product, options.forceFetchBasicData));
    }

    const fetchPrice = options.fetchPrice ?? true;
    const fetchAvailability = options.fetchAvailability ?? true;

    const pricePromise = fetchPrice ? dispatch(requestPriceData(code, priceReference, true)) : Promise.resolve([]);
    const availabilityPromise = fetchAvailability
      ? dispatch(requestAvailabilityData(code, true))
      : Promise.resolve(undefined);

    await Promise.all([pricePromise, availabilityPromise]);

    return selectProductDescriptor(getState(), { productCode: code, priceReference });
  };
}

export function requestBaseProductData(
  code: string,
  options: RequestFullProductDataOptions = {},
): ThunkResult<Promise<ProductData>> {
  return async (dispatch) => {
    const product = await fetchProductInfo(code);
    dispatch(addBaseProductDataAction(product, options.forceFetchBasicData));

    return product;
  };
}

export interface BulkPriceRequest {
  productCode: string;
  priceReference: string;
  packCode?: string;
}

export function requestBulkPriceData(items: BulkPriceRequest[]): ThunkResult<void> {
  return (dispatch, getState) => {
    if (!userHasPermission(Permission.PRICE)) {
      return;
    }

    const state = getState();
    const uncached = items.filter((item) => {
      const cacheKey = getCacheKey(item);
      const entry = state.products.price[cacheKey];
      return !entry?.data;
    });

    if (uncached.length === 0) {
      return;
    }

    const seen = new Set<string>();
    const deduped = uncached.filter((item) => {
      const key = getCacheKey(item);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    // Resolve canonical cache keys upfront (product.code may differ from item.productCode)
    const resolvedItems = deduped.map((item) => {
      const product = selectProductData(item.productCode)(getState());
      const cacheKey = getCacheKey({
        productCode: product?.code ?? item.productCode,
        priceReference: item.priceReference,
      });
      return { item, product, cacheKey };
    });

    const cacheKeys = resolvedItems.map((r) => r.cacheKey);
    dispatch(productsActions.price.bulkLoad(cacheKeys));

    // Register per-key deferred promises in pPromisesInFlight so that requestPriceData
    // can attach to in-flight bulk fetches instead of triggering duplicate network requests.
    const deferreds = new Map<string, { resolve: (data: PriceInfo[]) => void; reject: (reason: unknown) => void }>();
    for (const cacheKey of cacheKeys) {
      let resolve!: (data: PriceInfo[]) => void;
      let reject!: (reason: unknown) => void;
      pPromisesInFlight[cacheKey] = new Promise<PriceInfo[]>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      deferreds.set(cacheKey, { resolve, reject });
    }

    // Fire individual fetches. Each result feeds into the throttled flush queue,
    // which batches Redux updates every 160ms to avoid per-product re-renders.
    for (const { item, product, cacheKey } of resolvedItems) {
      const deferred = deferreds.get(cacheKey)!;

      (product?.type === ProductType.CONFIGURATOR
        ? getLinearPriceInfo(getHybrisProductCode(product.code), product.configId, item.priceReference)
        : requestPriceDataSingle({
            productCode: product?.pnaCode ?? item.productCode,
            priceReference: item.priceReference,
            packCode: item.packCode,
          })
      ).then(
        (data) => priceFlushQueue.enqueue({ cacheKey, result: { ok: true, data }, deferred }, dispatch),
        () => priceFlushQueue.enqueue({ cacheKey, result: { ok: false }, deferred }, dispatch),
      );
    }
  };
}

export interface BulkAvailabilityRequest {
  productCode: string;
  packCode?: string;
}

export function requestBulkAvailabilityData(items: BulkAvailabilityRequest[]): ThunkResult<void> {
  return (dispatch, getState) => {
    if (!userHasPermission(Permission.AVAILABILITY)) {
      return;
    }

    const state = getState();
    const uncached = items.filter((item) => {
      const cacheKey = getCacheKey({ productCode: item.productCode });
      const entry = state.products.availability[cacheKey];
      return !entry?.data;
    });

    if (uncached.length === 0) {
      return;
    }

    const seen = new Set<string>();
    const deduped = uncached.filter((item) => {
      if (seen.has(item.productCode)) {
        return false;
      }
      seen.add(item.productCode);
      return true;
    });

    // Resolve canonical cache keys upfront (product.code may differ from item.productCode)
    const resolvedItems = deduped.map((item) => {
      const product = selectProductData(item.productCode)(getState());
      const cacheKey = getCacheKey({ productCode: product?.code ?? item.productCode, priceReference: "" });
      return { item, product, cacheKey };
    });

    const cacheKeys = resolvedItems.map((r) => r.cacheKey);
    dispatch(productsActions.availability.bulkLoad(cacheKeys));

    // Register per-key deferred promises in aPromisesInFlight so that requestAvailabilityData
    // can attach to in-flight bulk fetches instead of triggering duplicate network requests.
    const deferreds = new Map<
      string,
      { resolve: (data: AvailabilityInfo) => void; reject: (reason: unknown) => void }
    >();
    for (const cacheKey of cacheKeys) {
      let resolve!: (data: AvailabilityInfo) => void;
      let reject!: (reason: unknown) => void;
      aPromisesInFlight[cacheKey] = new Promise<AvailabilityInfo>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      deferreds.set(cacheKey, { resolve, reject });
    }

    // Fire individual fetches. Each result feeds into the throttled flush queue,
    // which batches Redux updates every 160ms to avoid per-product re-renders.
    for (const { item, product, cacheKey } of resolvedItems) {
      const deferred = deferreds.get(cacheKey)!;

      (product?.type === ProductType.CONFIGURATOR
        ? getLinearAvailabilityInfo(product.pnaCode, product.configId)
        : requestAvailabilityDataSingle({
            productCode: product?.pnaCode ?? item.productCode,
            packCode: item.packCode,
          })
      ).then(
        (data) => availabilityFlushQueue.enqueue({ cacheKey, result: { ok: true, data }, deferred }, dispatch),
        () => availabilityFlushQueue.enqueue({ cacheKey, result: { ok: false }, deferred }, dispatch),
      );
    }
  };
}
